/**
 * <ev-pausable>: motion controller for every marquee, rotator, slider and
 * autoplay video in the Evident theme (WCAG 2.2.2, 2.3.3, 2.4.7, 2.4.11).
 *
 * Markup contract (see snippets/ev-pausable-toggle.liquid):
 *   <ev-pausable class="ev-pausable">
 *     <button type="button" data-ev-pause-toggle aria-pressed="false">   first child, outside the content
 *     <div data-ev-content> ... </div>                                    hover and focus are tracked here only
 *   </ev-pausable>
 * Inside [data-ev-content]:
 *   [data-ev-marquee]  a track whose children are the items; cloned for the loop
 *   [data-ev-rotator]  a live region whose children are the slides only; one has [data-active]
 *   [data-ev-dots]     optional slide controls, rendered once, OUTSIDE [data-ev-rotator]:
 *                      <button type="button" data-ev-slide-to="0..n-1"> each; the active
 *                      one carries aria-current="true". Hidden under [data-static], where
 *                      every slide shows, and before upgrade (assets/ev-motion.css).
 *   video[data-ev-autoplay]  muted playsinline loop with a poster, no autoplay attribute
 *                      and preload="none": it plays only while it intersects the viewport
 *                      (an IntersectionObserver), so an off-screen video never downloads
 *                      or runs; scrolling it out of view pauses it without touching
 *                      userPaused (final review B-m4)
 *
 * State: userPaused (the toggle, reduced motion) and paused (userPaused, or the
 * pointer or keyboard focus is inside the content). The toggle's aria-pressed
 * follows userPaused, [data-paused] follows paused. Event ev-pausable:change
 * bubbles with detail { paused, userPaused } whenever either value changes.
 *
 * goTo(index) shows slide `index` (wrapping), moves [data-active] and aria-current,
 * and dispatches ev-pausable:slide (bubbles) with detail { index }. The timer's
 * advance goes through goTo too. Focusing a dot pauses the rotator (it is inside the
 * content), which also turns on the rotator's polite live region.
 */
const REDUCED = '(prefers-reduced-motion: reduce)';
const FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex]';

class EvPausable extends HTMLElement {
  #userPaused = false;
  #hover = false;
  #focus = false;
  #lastPaused = null;
  #lastUserPaused = null;
  #mql = null;
  #timer = 0;
  #observer = null;
  #resizeFrame = 0;
  #ready = false;
  #rotatorPaused = null;
  #videoObserver = null;
  #visibleVideos = new WeakSet();

  get paused() {
    return this.#userPaused || this.#hover || this.#focus;
  }

  get userPaused() {
    return this.#userPaused;
  }

  set userPaused(value) {
    this.#userPaused = Boolean(value);
    this.#sync();
  }

  get #content() {
    return this.querySelector('[data-ev-content]') || this;
  }

  get #toggle() {
    return this.querySelector('[data-ev-pause-toggle]');
  }

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    this.#mql = window.matchMedia(REDUCED);
    this.#userPaused = this.#mql.matches;
    this.#mql.addEventListener('change', this.#onMotionPreference);

    this.#toggle?.addEventListener('click', this.#onToggle);
    this.addEventListener('click', this.#onDotClick);
    const content = this.#content;
    content.addEventListener('pointerenter', this.#onEnter);
    content.addEventListener('pointerleave', this.#onLeave);
    content.addEventListener('focusin', this.#onFocusIn);
    content.addEventListener('focusout', this.#onFocusOut);

    this.#initRotator();
    this.#buildMarquee();
    const track = this.querySelector('[data-ev-marquee]');
    if (track && 'ResizeObserver' in window) {
      this.#observer = new ResizeObserver(() => {
        cancelAnimationFrame(this.#resizeFrame);
        this.#resizeFrame = requestAnimationFrame(() => this.#buildMarquee());
      });
      this.#observer.observe(content);
    }
    this.#observeVideos();

    this.#sync(true);
  }

  disconnectedCallback() {
    if (!this.#ready) return;
    this.#ready = false;
    this.#mql?.removeEventListener('change', this.#onMotionPreference);
    this.#observer?.disconnect();
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;
    this.#visibleVideos = new WeakSet();
    cancelAnimationFrame(this.#resizeFrame);
    clearInterval(this.#timer);
    this.#timer = 0;
    this.#rotatorPaused = null;
    this.#hover = false;
    this.#focus = false;
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.removeEventListener('click', this.#onDotClick);
    const content = this.#content;
    content.removeEventListener('pointerenter', this.#onEnter);
    content.removeEventListener('pointerleave', this.#onLeave);
    content.removeEventListener('focusin', this.#onFocusIn);
    content.removeEventListener('focusout', this.#onFocusOut);
  }

  // ------------------------------------------------------------- state

  #onToggle = () => {
    this.userPaused = !this.#userPaused;
  };

  #onMotionPreference = (event) => {
    this.userPaused = event.matches;
  };

  #onEnter = () => {
    this.#hover = true;
    this.#sync();
  };

  #onLeave = () => {
    this.#hover = false;
    this.#sync();
  };

  #onFocusIn = (event) => {
    this.#focus = true;
    this.#sync();
    // The browser scrolled to the item against the moving layout; with the transform now
    // reset, bring the item into view again so it is fully visible.
    if (event.target.closest?.('[data-ev-marquee]')) {
      event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  };

  #onFocusOut = (event) => {
    // A null relatedTarget means focus left the document or went to nothing: out.
    const next = event.relatedTarget;
    this.#focus = Boolean(next && this.#content.contains(next));
    this.#sync();
  };

  #sync(initial = false) {
    const paused = this.paused;
    const reduced = this.#mql ? this.#mql.matches : false;

    this.toggleAttribute('data-paused', paused);
    this.toggleAttribute('data-user-play', !this.#userPaused);
    // Static presentation: reduced motion, and the user has not asked for movement.
    this.toggleAttribute('data-static', reduced && this.#userPaused);
    // Keyboard focus inside the marquee: drop the transform so the focus target is fully visible.
    const wasReset = this.hasAttribute('data-focus-reset');
    const inMarquee = this.#focus && Boolean(this.querySelector('[data-ev-marquee]')?.contains(document.activeElement));
    this.toggleAttribute('data-focus-reset', inMarquee);
    if (wasReset && !inMarquee) this.#content.scrollLeft = 0;

    const toggle = this.#toggle;
    if (toggle) toggle.setAttribute('aria-pressed', String(this.#userPaused));

    this.#syncRotator(paused);
    // Video pauses only on the user's say-so (R35), never on hover or focus.
    this.#syncVideos(this.#userPaused);

    const changed = paused !== this.#lastPaused || this.#userPaused !== this.#lastUserPaused;
    const first = this.#lastPaused === null;
    this.#lastPaused = paused;
    this.#lastUserPaused = this.#userPaused;
    if (changed && !first && !initial) {
      this.dispatchEvent(
        new CustomEvent('ev-pausable:change', {
          bubbles: true,
          detail: { paused, userPaused: this.#userPaused },
        }),
      );
    }
  }

  // ----------------------------------------------------------- marquee

  #buildMarquee() {
    const track = this.querySelector('[data-ev-marquee]');
    if (!track) return;
    track.querySelectorAll(':scope > [data-ev-clone]').forEach((node) => node.remove());
    const originals = [...track.children];
    if (!originals.length) return;

    let setWidth = 0;
    for (const item of originals) {
      const style = getComputedStyle(item);
      setWidth += item.getBoundingClientRect().width + parseFloat(style.marginInlineStart) + parseFloat(style.marginInlineEnd);
    }
    const view = this.#content.clientWidth;
    if (!setWidth || !view) return;

    // N identical sets are needed so that shifting by one set (100% / N) loops with no gap.
    const sets = Math.max(2, Math.ceil(view / setWidth) + 1);
    for (let i = 1; i < sets; i++) {
      for (const item of originals) {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-ev-clone', '');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
        if (clone.matches(FOCUSABLE)) clone.setAttribute('tabindex', '-1');
        clone.querySelectorAll(FOCUSABLE).forEach((n) => n.setAttribute('tabindex', '-1'));
        track.append(clone);
      }
    }
    const speed = Number(track.dataset.speed) || 50; // pixels per second
    track.style.setProperty('--ev-marquee-shift', `${(-100 / sets).toFixed(4)}%`);
    track.style.setProperty('--ev-marquee-duration', `${(setWidth / speed).toFixed(2)}s`);
  }

  // ----------------------------------------------------------- rotator

  #initRotator() {
    const rotator = this.querySelector('[data-ev-rotator]');
    if (!rotator) return;
    const slides = [...rotator.children];
    if (!slides.some((s) => s.hasAttribute('data-active')) && slides[0]) {
      slides[0].setAttribute('data-active', '');
    }
    rotator.setAttribute('aria-atomic', 'true');
    this.#syncDots(Math.max(0, slides.findIndex((s) => s.hasAttribute('data-active'))));
  }

  /**
   * Show slide `index` (wrapping at both ends). Moves [data-active] and the dots'
   * aria-current, then dispatches ev-pausable:slide with { index }. Returns the index shown.
   */
  goTo(index) {
    const rotator = this.querySelector('[data-ev-rotator]');
    if (!rotator) return -1;
    const slides = [...rotator.children];
    if (!slides.length) return -1;
    const next = ((Math.trunc(Number(index)) || 0) % slides.length + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === next));
    this.#syncDots(next);
    this.dispatchEvent(new CustomEvent('ev-pausable:slide', { bubbles: true, detail: { index: next } }));
    return next;
  }

  #syncDots(index) {
    for (const dot of this.querySelectorAll('[data-ev-slide-to]')) {
      if (dot.closest('ev-pausable') !== this) continue;
      if (Number(dot.dataset.evSlideTo) === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  #onDotClick = (event) => {
    const dot = event.target.closest?.('[data-ev-slide-to]');
    if (!dot || dot.closest('ev-pausable') !== this) return;
    this.goTo(Number(dot.dataset.evSlideTo));
  };

  #syncRotator(paused) {
    const rotator = this.querySelector('[data-ev-rotator]');
    if (!rotator) return;
    // Announce changes only while the user is not being moved along by a timer.
    rotator.setAttribute('aria-live', paused ? 'polite' : 'off');
    if (paused === this.#rotatorPaused) return;
    this.#rotatorPaused = paused;
    clearInterval(this.#timer);
    this.#timer = 0;
    if (!paused && rotator.children.length > 1) {
      const interval = Math.max(1000, Number(rotator.dataset.interval) || 5000);
      this.#timer = setInterval(() => this.#advance(rotator), interval);
    }
  }

  #advance(rotator) {
    const slides = [...rotator.children];
    const current = Math.max(0, slides.findIndex((s) => s.hasAttribute('data-active')));
    this.goTo(current + 1);
  }

  // ------------------------------------------------------------- video

  /**
   * Final review B-m4: track which autoplay videos intersect the viewport. Only those play;
   * the rest stay paused on their poster, unloaded (preload="none"). Without
   * IntersectionObserver every video counts as visible, as before.
   */
  #observeVideos() {
    const videos = [...this.querySelectorAll('video[data-ev-autoplay]')];
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      for (const video of videos) this.#visibleVideos.add(video);
      return;
    }
    this.#videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#visibleVideos.add(entry.target);
        else this.#visibleVideos.delete(entry.target);
      }
      this.#syncVideos(this.#userPaused);
    });
    for (const video of videos) this.#videoObserver.observe(video);
  }

  /**
   * Play the visible autoplay videos unless the user paused (the toggle or reduced motion,
   * R35: hover and focus never pause a video); pause the others. Leaving the viewport is
   * not a user pause, so a video comes back playing, and one the user paused stays paused.
   */
  #syncVideos(paused) {
    for (const video of this.querySelectorAll('video[data-ev-autoplay]')) {
      if (paused || !this.#visibleVideos.has(video)) {
        if (!video.paused) video.pause();
        continue;
      }
      let attempt;
      try {
        attempt = video.play();
      } catch {
        attempt = Promise.reject(new Error('play failed'));
      }
      if (attempt && typeof attempt.catch === 'function') {
        // Autoplay blocked (data saver, low power mode): stay on the poster and say so.
        attempt.catch((error) => {
          // AbortError only means our own pause() interrupted the request.
          if (error?.name === 'AbortError') return;
          if (!this.#userPaused) this.userPaused = true;
        });
      }
    }
  }
}

if (!customElements.get('ev-pausable')) customElements.define('ev-pausable', EvPausable);
